import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import MarketingNav from "@/components/Layout/MarketingNav";
import MarketingFooter from "@/components/Layout/MarketingFooter";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Check, ArrowUpRight } from "lucide-react";
import { fmtMoney } from "@/lib/format";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("yearly");
  const [currency, setCurrency] = useState("INR");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data || []));
  }, []);

  const priceFor = (p) => {
    const k = `${cycle}_price_${currency.toLowerCase()}`;
    return p[k];
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-12">
        <div className="label-eyebrow mb-4">Pricing</div>
        <div className="grid lg:grid-cols-3 gap-8 items-end mb-12">
          <h1 className="heading-display text-5xl sm:text-7xl lg:col-span-2">
            Simple, per-seat. Pay only for the internal team.
          </h1>
          <p className="text-muted-foreground">
            Internal users count toward seats. Client reviewers and guests are unlimited and free, forever.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-6 mb-12">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Monthly</span>
            <Switch
              checked={cycle === "yearly"}
              onCheckedChange={(v) => setCycle(v ? "yearly" : "monthly")}
              data-testid="pricing-cycle-toggle"
            />
            <span className="text-sm font-semibold">Yearly <span className="chip chip-approved ml-1">Save 20%</span></span>
          </div>
          <div className="flex border border-border">
            {["INR", "USD"].map((c) => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                className={`px-3 py-1.5 text-xs font-bold ${currency === c ? "bg-foreground text-background" : "hover:bg-muted"}`}
                data-testid={`pricing-currency-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-3">
          {plans.filter((p) => p.slug !== "enterprise").map((p) => {
            const featured = p.is_featured;
            const price = priceFor(p);
            return (
              <div
                key={p.slug}
                className={`border p-8 flex flex-col ${featured ? "border-primary bg-foreground text-background" : "border-border bg-background"}`}
                data-testid={`pricing-card-${p.slug}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="label-eyebrow" style={{ color: featured ? "rgba(255,255,255,0.7)" : undefined }}>
                    {p.name}
                  </div>
                  {featured && <span className="chip chip-approved">Most popular</span>}
                </div>
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="heading-display text-5xl">
                    {price === 0 ? "Free" : fmtMoney(price, currency)}
                  </span>
                  {price > 0 && (
                    <span className="text-sm opacity-70">/ {cycle === "yearly" ? "year" : "mo"}</span>
                  )}
                </div>
                <p className={`text-sm mb-6 ${featured ? "opacity-80" : "text-muted-foreground"}`}>{p.description}</p>
                <ul className="space-y-2 mb-8 text-sm flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button
                    className="rounded-none w-full h-11 font-bold uppercase tracking-wide text-xs"
                    variant={featured ? "default" : "outline"}
                    data-testid={`pricing-cta-${p.slug}`}
                  >
                    {p.slug === "free" ? "Start free" : "Choose plan"}
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
        {/* Enterprise */}
        {plans.find((p) => p.slug === "enterprise") && (
          <div className="mt-3 border border-border p-10 bg-foreground text-background grid md:grid-cols-3 items-center gap-6" data-testid="pricing-card-enterprise">
            <div>
              <div className="label-eyebrow opacity-70 mb-2">Enterprise</div>
              <h3 className="heading-display text-4xl">Custom for large teams.</h3>
            </div>
            <ul className="space-y-2 text-sm md:col-span-1">
              {(plans.find((p) => p.slug === "enterprise").features || []).map((f, i) => (
                <li key={i} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-primary" />{f}</li>
              ))}
            </ul>
            <div className="md:text-right">
              <Link to="/signup">
                <Button size="lg" className="rounded-none h-12 px-6 font-bold uppercase tracking-wide text-xs" data-testid="pricing-enterprise-cta">
                  Contact sales <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-8">
          GST invoices for Indian customers. INR is billed via Cashfree. Storage add-ons and seat upgrades are pro-rated.
        </p>
      </section>
      <MarketingFooter />
    </div>
  );
}
